/**
 * Action Executor - Executes validated database operations
 */

import { supabase } from '../supabase';
import { ParsedCommand, ExecutionResult } from './types';
import { formatExecutionResult } from './confirmationHandler';
import { checkRateLimit } from './rateLimiter';

// Import operation handlers
import { executeServiceRequestOperation } from './operations/serviceRequests';
import { executeAdminTaskOperation } from './operations/tasks';
import { executeUserOperation } from './operations/users';
import { executeInventoryOperation } from './operations/inventory';

export async function executeCommand(
  command: ParsedCommand,
  userId: string
): Promise<ExecutionResult> {
  const { intent } = command;

  try {
    // Check rate limit
    const rateLimitCheck = checkRateLimit(userId);
    if (!rateLimitCheck.allowed) {
      return {
        success: false,
        error: `Rate limit exceeded. Try again after ${rateLimitCheck.resetAt.toLocaleTimeString()}`,
        message: 'Too many requests',
      };
    }

    // Verify user is system manager
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('is_system_manager')
      .eq('id', userId)
      .single();

    if (profileError || !profile?.is_system_manager) {
      return {
        success: false,
        error: 'Unauthorized: Only system managers can use AI admin commands',
        message: 'Permission denied',
      };
    }

    // Route to appropriate operation handler
    let result: ExecutionResult;

    if (intent === 'help') {
      result = executeHelpCommand();
    } else if (intent === 'clarification_needed') {
      result = {
        success: false,
        error: command.clarificationNeeded,
        message: command.clarificationNeeded || 'Clarification needed',
      };
    } else if (intent.includes('service_request')) {
      result = await executeServiceRequestOperation(command, userId);
    } else if (intent.includes('admin_task')) {
      result = await executeAdminTaskOperation(command, userId);
    } else if (intent.includes('user')) {
      result = await executeUserOperation(command, userId);
    } else if (intent.includes('inventory')) {
      result = await executeInventoryOperation(command, userId);
    } else {
      result = {
        success: false,
        error: `Unsupported operation: ${intent}`,
        message: 'Operation not supported',
      };
    }

    // Log the execution
    await logExecution(command, userId, result);

    // Add formatted message if not already present
    if (!result.message && result.success) {
      result.message = formatExecutionResult(result.data, intent);
    }

    return result;
  } catch (error) {
    console.error('Error executing command:', error);
    
    const errorResult: ExecutionResult = {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      message: 'Failed to execute command',
    };

    // Log the failed execution
    await logExecution(command, userId, errorResult);

    return errorResult;
  }
}

function executeHelpCommand(): ExecutionResult {
  const helpText = `
**Available Commands:**

**Service Requests:**
- "Show me all pending service requests"
- "Mark service request #[id] as completed"
- "Change priority of request #[id] to urgent"

**Admin Tasks:**
- "Show me all urgent tasks"
- "Create a task for [user]'s home to [description]"
- "Mark task #[id] as completed"

**User Management:**
- "Show me info for user [name/email]"
- "List all users assigned to [home manager]"
- "Update lifecycle status for [user] to active"

**Inventory:**
- "What inventory items does [user] have?"
- "Show me all appliances with expired warranties"
- "Add [item name] to [user]'s home inventory"
`;

  return {
    success: true,
    data: { helpText },
    message: helpText,
  };
}

async function logExecution(
  command: ParsedCommand,
  userId: string,
  result: ExecutionResult
): Promise<void> {
  try {
    await supabase.from('ai_admin_logs').insert({
      user_id: userId,
      command_text: command.originalInput,
      parsed_intent: command.intent,
      parsed_entities: command.entities,
      execution_success: result.success,
      execution_result: result.success ? result.data : null,
      execution_error: result.error || null,
    });
  } catch (error) {
    console.error('Failed to log AI admin execution:', error);
    // Don't throw - logging failure shouldn't break the command
  }
}

export async function validateAndPreviewCommand(
  command: ParsedCommand,
  userId: string
): Promise<{ valid: boolean; previewData?: any; error?: string }> {
  const { intent, entities } = command;

  try {
    // For update operations, fetch the target record to show preview
    if (intent === 'update_service_request' && entities.target_id) {
      const { data, error } = await supabase
        .from('service_requests')
        .select('id, title, status, priority')
        .eq('id', entities.target_id)
        .single();

      if (error || !data) {
        return { valid: false, error: 'Service request not found' };
      }

      return { valid: true, previewData: data };
    }

    if (intent === 'update_admin_task' && entities.target_id) {
      const { data, error } = await supabase
        .from('admin_tasks')
        .select('id, title, status, priority')
        .eq('id', entities.target_id)
        .single();

      if (error || !data) {
        return { valid: false, error: 'Admin task not found' };
      }

      return { valid: true, previewData: data };
    }

    if (intent === 'update_user' && (entities.target_id || entities.target_email)) {
      const query = supabase
        .from('profiles')
        .select('id, full_name, lifecycle_status, home_manager');

      if (entities.target_id) {
        query.eq('id', entities.target_id);
      } else if (entities.target_email) {
        // Need to join with auth.users to search by email
        // For now, search by full_name if target_user is provided
        if (entities.target_user) {
          query.ilike('full_name', `%${entities.target_user}%`);
        }
      }

      const { data, error } = await query.single();

      if (error || !data) {
        return { valid: false, error: 'User not found' };
      }

      return { valid: true, previewData: data };
    }

    // For queries, count the results
    if (intent.startsWith('query_')) {
      // Queries are always valid, we'll just show count
      return { valid: true, previewData: { count: '?' } };
    }

    return { valid: true };
  } catch (error) {
    console.error('Error validating command:', error);
    return {
      valid: false,
      error: error instanceof Error ? error.message : 'Validation failed',
    };
  }
}
